import { SUPPORTED_LANGUAGES } from '@/stores/editorStore';
import type { Problem } from '@/lib/api/problems';
import { STUB_LANGUAGES, supportsC } from './stub';

/**
 * Determines which languages a student may pick for this problem.
 * sql-mode and prompt-mode problems only ever offer their own pseudo-language
 * (there is no meaningful "solve in Python" for a query-grading or
 * prompt-grading problem); function-mode problems restrict to STUB_LANGUAGES
 * as before; stdio and undefined offer everything EXCEPT sql/prompt.
 *
 * sql/prompt are deliberately excluded from the stdio/undefined list: the
 * editor's language selection is persisted globally (zustand
 * `kodprep-editor`), not per-problem, so if sql/prompt were selectable on a
 * stdio problem a student who just solved a SQL or Prompt Arena problem would
 * silently carry that language into the next ordinary problem and submit an
 * unrunnable language against it.
 */
export function resolveAvailableLanguages(problem?: Problem) {
  if (problem?.execution_mode === 'sql') {
    return SUPPORTED_LANGUAGES.filter((l) => l.value === 'sql');
  }
  if (problem?.execution_mode === 'prompt') {
    return SUPPORTED_LANGUAGES.filter((l) => l.value === 'prompt');
  }
  if (problem?.execution_mode === 'function') {
    // C is dropped when the signature uses a type its stub/driver cannot
    // express — C has no dynamic-type fallback, so offering it there would
    // hand the student a stub that cannot compile. Every other stub language
    // covers the whole vocabulary. A function-mode problem with no signature
    // at all can't be stubbed in C either.
    const stubbable = STUB_LANGUAGES.filter(
      (value) => value !== 'c' || (problem.signature ? supportsC(problem.signature) : false),
    );
    // Preserve STUB_LANGUAGES's own order (python first) rather than
    // SUPPORTED_LANGUAGES's order, so availableLanguages[0] stays the
    // intended default fallback language.
    return stubbable.map((value) =>
      SUPPORTED_LANGUAGES.find((l) => l.value === value),
    ).filter((l): l is (typeof SUPPORTED_LANGUAGES)[number] => l !== undefined);
  }
  return SUPPORTED_LANGUAGES.filter((l) => l.value !== 'sql' && l.value !== 'prompt');
}

/**
 * Picks the language the editor should show for this problem, given the
 * globally persisted selection. Keeps the current language when the problem
 * supports it and either authors no starter code at all or authors one for
 * that language. When the problem authors starter code only for other
 * languages (e.g. a JavaScript-only course problem), switches to the first
 * authored one — otherwise a student whose last language was C++ lands on a
 * generated C++ stub for a problem written around JavaScript semantics.
 */
export function resolvePreferredLanguage(
  problem: Problem | undefined,
  available: { value: string }[],
  current: string,
): string {
  const values = available.map((l) => l.value);
  const authored = Object.entries(problem?.starter_code ?? {})
    .filter(([lang, code]) => code?.trim() && values.includes(lang))
    .map(([lang]) => lang);
  if (values.includes(current) && (authored.length === 0 || authored.includes(current))) {
    return current;
  }
  return authored[0] ?? values[0] ?? current;
}
